import React, { useState, useContext } from 'react';
import { AuthContext } from '../AuthContext';
import { useTheme } from '../ThemeContext';
import './Login.css';
import '../shared/PageHeader.css';
import { url as API_BASE_URL } from '../../MongoDb/MongoConstants';

function Login() {
  const [showRegister, setShowRegister] = useState(false);
  const [showReset, setShowReset] = useState(false);
  const { isDarkMode } = useTheme();

  return (
    <div className="login-page-bg">
      <div className="login-card">
        <h2 className="login-title">
          {showReset ? "Reset Password" : showRegister ? "Register" : "Login"}
        </h2>
        {showReset ? (
          <PasswordResetForm onBack={() => setShowReset(false)} />
        ) : showRegister ? (
          <RegisterForm onRegister={() => setShowRegister(false)} />
        ) : (
          <>
            <LoginForm />
            <div style={{ textAlign: "right", marginTop: "-0px", marginBottom: "10px" }}>
              <button
                className="btn btn-link p-0 login-toggle-btn"
                style={{ fontSize: "0.95rem" }}
                onClick={() => setShowReset(true)}
              >
                Forgot password?
              </button>
            </div>
          </>
        )}
        <div className="login-toggle">
          {showReset ? null : showRegister ? (
            <span>
              Already have an account?{' '}
              <button className="btn btn-link p-0 login-toggle-btn" onClick={() => setShowRegister(false)}>Login</button>
            </span>
          ) : (
            <span>
              New user?{' '}
              <button className="btn btn-link p-0 login-toggle-btn" onClick={() => setShowRegister(true)}>Register</button>
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

function LoginForm() {
  const [loginMethod, setLoginMethod] = useState('email'); // 'email' or 'phone'
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [codeSent, setCodeSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const { login } = useContext(AuthContext);
  const maintenance = false;

  const API_URL = API_BASE_URL; // or your API URL

  const formatPhone = (value) => {
    const digits = value.replace(/\D/g, '');
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
  };

  const formatPhoneForAPI = (formattedPhone) => {
    // Convert (555) 555-5555 to +15555555555
    const digits = formattedPhone.replace(/\D/g, '');
    return `+1${digits}`;
  };

  const handleSendCode = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccessMessage('');

    try {
      const response = await fetch(`${API_URL}/api/user/login/phone`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: formatPhoneForAPI(phone) })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to send code');
      }

      setCodeSent(true);
      setSuccessMessage('Verification code sent to your phone!');
    } catch (err) {
      setError(err.message || 'Failed to send verification code');
    } finally {
      setLoading(false);
    }
  };

  const handlePhoneLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_URL}/api/user/login/phone/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          phone: formatPhoneForAPI(phone), 
          code: verificationCode 
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Verification failed');
      }

      // Store user data and redirect
      localStorage.setItem('userId', data.user_id);
      localStorage.setItem('userEmail', data.email);
      localStorage.setItem('userName', data.name);
      window.location.href = '/myaccount';
    } catch (err) {
      setError(err.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      await login(email, password, 'email');
      window.location.href = '/myaccount';
    } catch (err) {
      setError(err.message || "Login failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = () => {
    setCodeSent(false);
    setVerificationCode('');
    setSuccessMessage('');
  };

  return (
    <form onSubmit={loginMethod === 'email' ? handleEmailLogin : (codeSent ? handlePhoneLogin : handleSendCode)} className="login-form">
      {maintenance && (
        <div className="maintenance-note" style={{ color: "red", fontSize: 20, marginBottom: "12px", fontWeight: "bold", textAlign: "center" }}>
          The site is currently under maintenance. Login is temporarily disabled.
        </div>
      )}
      
      {error && (
        <div className="alert alert-danger" style={{ marginBottom: "12px" }}>
          {error}
        </div>
      )}

      {successMessage && (
        <div className="alert alert-success" style={{ marginBottom: "12px" }}>
          {successMessage}
        </div>
      )}

      {/* Login Method Tabs */}
      <div className="login-tabs">
        <button
          type="button"
          className={`login-tab ${loginMethod === 'phone' ? 'active' : ''}`}
          onClick={() => {
            setLoginMethod('phone');
            setCodeSent(false);
            setError('');
            setSuccessMessage('');
          }}
        >
          📱 Phone
        </button>

        <button
          type="button"
          className={`login-tab ${loginMethod === 'email' ? 'active' : ''}`}
          onClick={() => {
            setLoginMethod('email');
            setCodeSent(false);
            setError('');
            setSuccessMessage('');
          }}
        >
          📧 Email
        </button>
      </div>

      {loginMethod === 'email' ? (
        <>
          <div className="form-group">
            <label>Email</label>
            <input
              className="form-control"
              type="email"
              placeholder="Enter your email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              required
              disabled={maintenance || loading}
            />
          </div>
          <div className="form-group">
            <label>Password</label>
            <input
              className="form-control"
              type="password"
              placeholder="Enter your password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              required
              disabled={maintenance || loading}
            />
          </div>
          <button 
            className="btn btn-primary w-100 login-btn" 
            type="submit" 
            disabled={maintenance || loading}
          >
            {loading ? "Logging in..." : "Login"}
          </button>
        </>
      ) : (
        <>
          <div className="form-group">
            <label>Phone Number</label>
            <input
              className="form-control"
              type="tel"
              placeholder="(555) 555-5555"
              value={phone}
              onChange={e => setPhone(formatPhone(e.target.value))}
              required
              disabled={maintenance || loading || codeSent}
              maxLength={14}
            />
          </div>

          {codeSent && (
            <div className="form-group">
              <label>Verification Code</label>
              <input
                className="form-control"
                type="text"
                placeholder="Enter 4-digit code"
                value={verificationCode}
                onChange={e => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 4))}
                required
                disabled={maintenance || loading}
                maxLength={4}
                style={{ letterSpacing: '0.5em', textAlign: 'center', fontSize: '1.2em' }}
              />
              <small style={{ color: "var(--text-secondary)" }}>
                Didn't receive a code?{' '}
                <button 
                  type="button" 
                  className="btn btn-link p-0" 
                  onClick={handleResendCode}
                  style={{ fontSize: 'inherit' }}
                >
                  Resend
                </button>
              </small>
            </div>
          )}

          <button 
            className="btn btn-primary w-100 login-btn" 
            type="submit" 
            disabled={maintenance || loading}
          >
            {loading ? "Please wait..." : codeSent ? "Login" : "Send Code"}
          </button>
        </>
      )}
    </form>
  );
}

function SuccessModal({ show, onClose }) {
  if (!show) return null;

  return (
    <>
      <div className="modal-backdrop" onClick={onClose}></div>
      <div className="modal-container success-modal">
        <div className="modal-content">
          <div className="modal-header">
            <h2 className="modal-title">Registration Successful! 🎉</h2>
          </div>
          <div className="modal-body">
            <p>Your account has been created successfully.</p>
            <p>You're being logged in now...</p>
            <div className="spinner-container">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden"></span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

function RegisterForm({ onRegister }) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [phone, setPhone] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [codeSent, setCodeSent] = useState(false);
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [loading, setLoading] = useState(false);
  const [phoneLoading, setPhoneLoading] = useState(false);
  const [error, setError] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [phoneSuccess, setPhoneSuccess] = useState('');
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const { register } = useContext(AuthContext);

  const API_URL = API_BASE_URL;

  const formatPhone = (value) => {
    const digits = value.replace(/\D/g, '');
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
  };

  const formatPhoneForAPI = (formattedPhone) => {
    const digits = formattedPhone.replace(/\D/g, '');
    return `+1${digits}`;
  };

  const handleSendCode = async () => {
    if (phone.replace(/\D/g, '').length !== 10) {
      setPhoneError('Please enter a valid 10-digit phone number');
      return;
    }

    setPhoneLoading(true);
    setPhoneError('');
    setPhoneSuccess('');

    try {
      const response = await fetch(`${API_URL}/api/user/register/phone/send-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: formatPhoneForAPI(phone) })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to send code');
      }

      setCodeSent(true);
      setPhoneSuccess('Verification code sent!');
    } catch (err) {
      setPhoneError(err.message || 'Failed to send verification code');
    } finally {
      setPhoneLoading(false);
    }
  };

  const handleVerifyCode = async () => {
    if (verificationCode.length !== 4) {
      setPhoneError('Please enter the 4-digit code');
      return;
    }

    setPhoneLoading(true);
    setPhoneError('');

    try {
      const response = await fetch(`${API_URL}/api/user/register/phone/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          phone: formatPhoneForAPI(phone), 
          code: verificationCode 
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Verification failed');
      }

      setPhoneVerified(true);
      setPhoneSuccess('Phone number verified! ✓');
    } catch (err) {
      setPhoneError(err.message || 'Invalid code');
    } finally {
      setPhoneLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    
    // Require at least email OR verified phone
    const hasEmail = email.trim().length > 0;
    const hasPhone = phone.replace(/\D/g, '').length === 10;

    if (!hasEmail && !hasPhone) {
      setError("Please provide an email address or phone number.");
      return;
    }

    // If phone provided but not verified, show error
    if (hasPhone && !phoneVerified) {
      setError("Please verify your phone number before registering.");
      return;
    }

    // If email provided, require password
    if (hasEmail) {
      if (password !== confirm) {
        setError("Passwords do not match.");
        return;
      }
      if (password.length < 6) {
        setError("Password must be at least 6 characters.");
        return;
      }
    }

    if (name.trim().length < 2) {
      setError("Name must be at least 2 characters.");
      return;
    }

    setLoading(true);
    setError('');

    try {
      await register(email, password, name, hasPhone ? formatPhoneForAPI(phone) : null);
      setShowSuccessModal(true);
      setTimeout(() => {
        window.location.href = '/myaccount';
      }, 2000);
    } catch (err) {
      setError(err.message || "Registration failed. Please try again.");
      console.error("Registration error:", err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <form onSubmit={handleRegister} className="login-form">
        {error && (
          <div className="alert alert-danger" style={{ marginBottom: "12px" }}>
            {error}
          </div>
        )}

        <div className="form-group">
          <label>Full Name <span style={{ color: 'red' }}>*</span></label>
          <input
            className="form-control"
            type="text"
            placeholder="Enter your full name"
            value={name}
            onChange={e => setName(e.target.value)}
            required
            disabled={loading}
          />
        </div>

        {/* Phone Section */}
        <div className="register-section">
          <div className="section-header">
            <span>📱 Phone Login</span>
            <small style={{ color: 'var(--text-secondary)' }}>(optional if using email)</small>
          </div>

          {phoneError && (
            <div className="alert alert-danger" style={{ marginBottom: "12px", padding: '8px 12px', fontSize: '0.9rem' }}>
              {phoneError}
            </div>
          )}

          {phoneSuccess && (
            <div className="alert alert-success" style={{ marginBottom: "12px", padding: '8px 12px', fontSize: '0.9rem' }}>
              {phoneSuccess}
            </div>
          )}

          <div className="form-group">
            <label>Phone Number</label>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <input
                className="form-control"
                type="tel"
                placeholder="(555) 555-5555"
                value={phone}
                onChange={e => {
                  setPhone(formatPhone(e.target.value));
                  setCodeSent(false);
                  setPhoneVerified(false);
                  setPhoneSuccess('');
                }}
                disabled={loading || phoneVerified}
                maxLength={14}
                style={{ flex: 1 }}
              />
              {!phoneVerified && (
                <button
                  type="button"
                  className="btn btn-outline-primary"
                  onClick={handleSendCode}
                  disabled={phoneLoading || phone.replace(/\D/g, '').length !== 10}
                  style={{ whiteSpace: 'nowrap' }}
                >
                  {phoneLoading ? '...' : codeSent ? 'Resend' : 'Send Code'}
                </button>
              )}
              {phoneVerified && (
                <span style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  color: '#10b981',
                  fontWeight: 'bold',
                  padding: '0 0.5rem'
                }}>
                  ✓
                </span>
              )}
            </div>
          </div>

          {codeSent && !phoneVerified && (
            <div className="form-group">
              <label>Verification Code</label>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <input
                  className="form-control"
                  type="text"
                  placeholder="Enter 4-digit code"
                  value={verificationCode}
                  onChange={e => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 4))}
                  disabled={phoneLoading}
                  maxLength={4}
                  style={{ 
                    letterSpacing: '0.5em', 
                    textAlign: 'center', 
                    fontSize: '1.2em',
                    flex: 1
                  }}
                />
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleVerifyCode}
                  disabled={phoneLoading || verificationCode.length !== 4}
                >
                  {phoneLoading ? '...' : 'Verify'}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Divider */}
        <div style={{ 
          textAlign: 'center', 
          margin: '1.5rem 0', 
          color: 'var(--text-secondary)',
          position: 'relative'
        }}>
          <span style={{ 
            background: 'var(--bg-primary)', 
            padding: '0 1rem',
            position: 'relative',
            zIndex: 1
          }}>
            and/or
          </span>
          <div style={{
            position: 'absolute',
            top: '50%',
            left: 0,
            right: 0,
            height: '1px',
            background: 'var(--border-primary)',
            zIndex: 0
          }}></div>
        </div>

        {/* Email Section */}
        <div className="register-section">
          <div className="section-header">
            <span>📧 Email Login</span>
            <small style={{ color: 'var(--text-secondary)' }}>(optional if using phone)</small>
          </div>
          
          <div className="form-group">
            <label>Email</label>
            <input
              className="form-control"
              type="email"
              placeholder="Enter your email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              disabled={loading}
            />
          </div>

          {email && (
            <>
              <div className="form-group">
                <label>Password</label>
                <input
                  className="form-control"
                  type="password"
                  placeholder="Create a password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  disabled={loading}
                />
                <small style={{ color: "var(--text-secondary)" }}>
                  Password must be at least 6 characters
                </small>
              </div>

              <div className="form-group">
                <label>Confirm Password</label>
                <input
                  className="form-control"
                  type="password"
                  placeholder="Confirm password"
                  value={confirm}
                  onChange={e => setConfirm(e.target.value)}
                  disabled={loading}
                />
              </div>
            </>
          )}
        </div>

        <button 
          className="btn btn-success w-100 login-btn" 
          type="submit" 
          disabled={loading}
          style={{ marginTop: '1.5rem' }}
        >
          {loading ? "Creating Account..." : "Register"}
        </button>

      <small style={{ display: "block", marginTop: "10px", color: "var(--text-secondary)" }}>
        By registering, you agree to our <a href="/terms" style={{ color: 'var(--accent-primary)' }}>terms</a> and <a href="/privacy" style={{ color: 'var(--accent-primary)' }}>privacy policy</a>.
      </small>
      </form>

      <SuccessModal 
        show={showSuccessModal} 
        onClose={() => setShowSuccessModal(false)} 
      />
    </>
  );
}

function PasswordResetForm({ onBack }) {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleReset = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // For now, just show a message - you'll need to implement password reset in backend
      setSent(true);
    } catch (err) {
      setError(err.message || "Failed to send reset email. Please try again.");
      console.error("Password reset error:", err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      {sent ? (
        <div style={{ textAlign: "center" }}>
          <div className="alert alert-success">
            Password reset functionality coming soon!
            <br/>For now, please contact support to reset your password.
          </div>
          <button 
            className="btn btn-primary w-100" 
            onClick={onBack}
          >
            Back to Login
          </button>
        </div>
      ) : (
        <form onSubmit={handleReset} className="login-form">
          {error && (
            <div className="alert alert-danger" style={{ marginBottom: "12px" }}>
              {error}
            </div>
          )}

          <div className="form-group">
            <label>Email Address</label>
            <input
              className="form-control"
              type="email"
              placeholder="Enter your email address"
              value={email}
              onChange={e => setEmail(e.target.value)}
              required
              disabled={loading}
            />
            <small style={{ color: "var(--text-secondary)" }}>
              We'll send a reset link to this email address
            </small>
          </div>

          <button 
            className="btn btn-warning w-100 login-btn" 
            type="submit" 
            disabled={loading}
          >
            {loading ? "Sending..." : "Send Password Reset Email"}
          </button>

          <button 
            className="btn btn-link mt-2 w-100" 
            type="button" 
            onClick={onBack}
          >
            Back to Login
          </button>
        </form>
      )}
    </div>
  );
}

export default Login;